import React, { useState, useEffect } from 'react';
import './App.professional.css';
import MenuBrowser from './MenuBrowser';
import CredentialsManager from './CredentialsManager';
import ProductCatalog from './ProductCatalog';
import TableViews from './TableViews';
import ProductDetailsView from './ProductDetailsView';
import ProductInsights from './ProductInsights';
import DatabaseStats from './DatabaseStats';
import ProductSync from './ProductSync';
import MenuSync from './MenuSync';
import Modifiers from './Modifiers';
import Configuration from './Configuration';

export default function App() {
  const [page, setPage] = useState('sync');
  const [restaurantId, setRestaurantId] = useState('');
  const [bearerToken, setBearerToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [messageType, setMessageType] = useState(null);
  const [result, setResult] = useState(null);
  const [savedLocations, setSavedLocations] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const [selectedLocationName, setSelectedLocationName] = useState('');

  useEffect(() => {
    const initializeLocations = async () => {
      try {
        const response = await fetch('/api/credentials');
        const locations = await response.json();
        setSavedLocations(locations);

        if (locations.length > 0) {
          await handleSelectLocation(locations[0].id);
        }
      } catch (error) {
        console.error('Error initializing locations:', error);
      }
    };
    initializeLocations();
  }, []);

  async function fetchLocations() {
    try {
      const response = await fetch('/api/credentials');
      const data = await response.json();
      setSavedLocations(data);
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  }

  async function handleSelectLocation(locationId) {
    setSelectedLocationId(locationId);
    try {
      const response = await fetch(`/api/credentials/${locationId}`);
      const data = await response.json();
      setSelectedLocationName(data.name);
      setRestaurantId(data.restaurant_id);
      setBearerToken(data.bearer_token);
      setMessage(null);
      setResult(null);
    } catch (error) {
      console.error('Error loading credentials:', error);
    }
  }

  async function handleSync(e) {
    e.preventDefault();
    setLoading(true);
    setMessage(null);
    setResult(null);

    try {
      const response = await fetch('/api/sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ restaurantId, bearerToken })
      });

      const data = await response.json();

      if (!response.ok) {
        setMessageType('error');
        setMessage(data.error || 'Sync failed');
      } else {
        setMessageType('success');
        setMessage('Sync successful!');
        setResult(data.summary);
      }
    } catch (error) {
      setMessageType('error');
      setMessage(error.message || 'Network error');
    } finally {
      setLoading(false);
    }
  }

  async function handleClearDatabase() {
    if (!window.confirm('⚠️ Are you sure you want to clear all database data? You will need to sync again to repopulate.')) {
      return;
    }

    setLoading(true);
    setMessage(null);
    setResult(null);

    try {
      const response = await fetch('/api/clear-database', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await response.json();

      if (!response.ok) {
        setMessageType('error');
        setMessage(data.error || 'Clear failed');
      } else {
        setMessageType('success');
        setMessage(data.message || 'Database cleared successfully');
        setResult(null);
      }
    } catch (error) {
      setMessageType('error');
      setMessage(error.message || 'Network error');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="app">
      {/* Sidebar Navigation */}
      <aside className="app-sidebar">
        <div className="sidebar-header">
          <div className="sidebar-logo">🍽️</div>
          <div className="sidebar-logo-text">
            <h1>Toast Menu</h1>
            <p>Management</p>
          </div>
        </div>

        <nav className="sidebar-nav">
          <button
            className={`sidebar-nav-item ${page === 'sync' ? 'active' : ''}`}
            onClick={() => setPage('sync')}
            title="Sync menu data from Toast API"
          >
            <span className="material-symbols-outlined">sync</span>
            <span>Sync</span>
          </button>
          <button
            className={`sidebar-nav-item ${page === 'product-sync' ? 'active' : ''}`}
            onClick={() => setPage('product-sync')}
            title="Sync products without modifiers to Firestore"
          >
            <span className="material-symbols-outlined">cloud_upload</span>
            <span>Product Sync</span>
          </button>
          <button
            className={`sidebar-nav-item ${page === 'menu-sync' ? 'active' : ''}`}
            onClick={() => setPage('menu-sync')}
            title="Sync menus and menu groups to Firestore"
          >
            <span className="material-symbols-outlined">sync_alt</span>
            <span>Menu Sync</span>
          </button>
          <button
            className={`sidebar-nav-item ${page === 'credentials' ? 'active' : ''}`}
            onClick={() => setPage('credentials')}
            title="Manage restaurant locations"
          >
            <span className="material-symbols-outlined">location_on</span>
            <span>Locations</span>
          </button>
          <button
            className={`sidebar-nav-item ${page === 'menu' ? 'active' : ''}`}
            onClick={() => setPage('menu')}
            title="Browse menu hierarchy"
          >
            <span className="material-symbols-outlined">restaurant_menu</span>
            <span>Menu Browser</span>
          </button>
          <button
            className={`sidebar-nav-item ${page === 'products' ? 'active' : ''}`}
            onClick={() => setPage('products')}
            title="View product catalog"
          >
            <span className="material-symbols-outlined">inventory_2</span>
            <span>Products</span>
          </button>
          <button
            className={`sidebar-nav-item ${page === 'tables' ? 'active' : ''}`}
            onClick={() => setPage('tables')}
            title="View data tables"
          >
            <span className="material-symbols-outlined">table_chart</span>
            <span>Tables</span>
          </button>
          <button
            className={`sidebar-nav-item ${page === 'product-details' ? 'active' : ''}`}
            onClick={() => setPage('product-details')}
            title="View product details"
          >
            <span className="material-symbols-outlined">info</span>
            <span>Product Details</span>
          </button>
          <button
            className={`sidebar-nav-item ${page === 'insights' ? 'active' : ''}`}
            onClick={() => setPage('insights')}
            title="View analytics insights"
          >
            <span className="material-symbols-outlined">analytics</span>
            <span>Insights</span>
          </button>
          <button
            className={`sidebar-nav-item ${page === 'db-stats' ? 'active' : ''}`}
            onClick={() => setPage('db-stats')}
            title="View database row counts"
          >
            <span className="material-symbols-outlined">database</span>
            <span>DB Stats</span>
          </button>
          <button
            className={`sidebar-nav-item ${page === 'modifiers' ? 'active' : ''}`}
            onClick={() => setPage('modifiers')}
            title="Browse modifier groups and their connected products"
          >
            <span className="material-symbols-outlined">tune</span>
            <span>Modifiers</span>
          </button>
          <button
            className={`sidebar-nav-item ${page === 'configuration' ? 'active' : ''}`}
            onClick={() => setPage('configuration')}
            title="Configure Firestore root collection and image bucket"
          >
            <span className="material-symbols-outlined">settings</span>
            <span>Configuration</span>
          </button>
        </nav>
      </aside>

      <div className="app-main">
        {/* Top Header */}
        <header className="app-header">
          <div className="header-search">
            <span className="material-symbols-outlined" style={{ position: 'absolute', left: '12px', color: '#3c4a42' }}>search</span>
            <input type="text" placeholder="Search..." style={{ paddingLeft: '40px' }} />
          </div>
          <div className="header-actions">
            <button className="header-button" title="Notifications">
              <span className="material-symbols-outlined">notifications</span>
            </button>
            <button className="header-button" title="Settings">
              <span className="material-symbols-outlined">settings</span>
            </button>
            {selectedLocationName && (
              <div className="header-location">
                <span className="header-location-text">📍 {selectedLocationName}</span>
              </div>
            )}
          </div>
        </header>

        {/* Main Content */}
        <div className="app-content">
        {page === 'sync' && (
          <div className="card">
            <p className="subtitle">Sync your Toast restaurant menu data into SQLite</p>

            {savedLocations.length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <div className="form-group">
                  <label htmlFor="locationSelect">📍 Select Saved Location</label>
                  <select
                    id="locationSelect"
                    value={selectedLocationId}
                    onChange={(e) => handleSelectLocation(e.target.value)}
                  >
                    <option value="">-- Choose a location --</option>
                    {savedLocations.map(loc => (
                      <option key={loc.id} value={loc.id}>
                        {loc.name}
                      </option>
                    ))}
                  </select>
                </div>

                {selectedLocationId && selectedLocationName && (
                  <div className="location-card">
                    <div className="location-header">
                      <span className="location-badge">🏪</span>
                      <div>
                        <div className="location-selected-name">{selectedLocationName}</div>
                        <div className="location-selected-id">ID: {restaurantId.substring(0, 8)}...{restaurantId.substring(restaurantId.length - 8)}</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            <form onSubmit={handleSync} className="form">
              <div className="form-group">
                <label htmlFor="restaurantId">Toast Restaurant External ID</label>
                <input
                  id="restaurantId"
                  type="text"
                  value={restaurantId}
                  onChange={(e) => setRestaurantId(e.target.value)}
                  placeholder="e.g., 9f7e57e9-cbae-4403-a552-248395953acc"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="bearerToken">Bearer Token</label>
                <input
                  id="bearerToken"
                  type="password"
                  value={bearerToken}
                  onChange={(e) => setBearerToken(e.target.value)}
                  placeholder="Your Toast API token"
                  required
                />
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button type="submit" disabled={loading} className="btn-primary">
                  {loading ? '⏳ Syncing...' : '🔄 Sync Now'}
                </button>
                <button
                  type="button"
                  disabled={loading}
                  onClick={handleClearDatabase}
                  className="btn-danger"
                  title="Clear all database data"
                >
                  🗑️ Clear DB
                </button>
              </div>
            </form>

            {message && (
              <div className={`message ${messageType}`}>
                {messageType === 'success' ? '✓' : '✕'} {message}
              </div>
            )}

            {result && (
              <div className="result">
                <h3>Sync Summary</h3>
                <div className="stats">
                  <div className="stat">
                    <span className="stat-label">Menus</span>
                    <span className="stat-value">{result.menus}</span>
                  </div>
                  <div className="stat">
                    <span className="stat-label">Groups</span>
                    <span className="stat-value">{result.groups}</span>
                  </div>
                  <div className="stat">
                    <span className="stat-label">Items</span>
                    <span className="stat-value">{result.items}</span>
                  </div>
                </div>
                <p className="success-text">✓ Data stored in SQLite database successfully!</p>
              </div>
            )}

            <div className="info">
              <p><strong>Backend:</strong> {window.location.origin}/api</p>
              <p><strong>Database:</strong> server/data/menu.db</p>
            </div>
          </div>
        )}

        {page === 'credentials' && (
          <CredentialsManager onLocationsUpdated={fetchLocations} />
        )}

        {page === 'menu' && (
          <MenuBrowser locationId={restaurantId} locationName={selectedLocationName} />
        )}

        {page === 'products' && (
          <ProductCatalog locationId={restaurantId} locationName={selectedLocationName} />
        )}

        {page === 'tables' && (
          <TableViews locationId={restaurantId} locationName={selectedLocationName} />
        )}

        {page === 'product-details' && (
          <ProductDetailsView locationId={restaurantId} locationName={selectedLocationName} />
        )}

        {page === 'insights' && (
          <ProductInsights locationId={restaurantId} locationName={selectedLocationName} />
        )}

        {page === 'db-stats' && (
          <DatabaseStats locationId={restaurantId} locationName={selectedLocationName} />
        )}

        {page === 'product-sync' && <ProductSync />}

        {page === 'menu-sync' && <MenuSync />}

        {page === 'modifiers' && <Modifiers locationId={restaurantId} locationName={selectedLocationName} />}
        {page === 'configuration' && <Configuration />}
        </div>
      </div>
    </div>
  );
}
